import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  getSHUCalculation,
  calculateSHU,
  approveSHU,
  distributeSHU,
  postSHU,
  formatCurrency,
  getCalculationStatusColor,
  getDistributionStatusColor,
  getStatusLabel,
  getPaymentMethodLabel
} from '../../../services/shu.service';

const SHUCalculationDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [calculation, setCalculation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  
  // Modals
  const [showCalculateModal, setShowCalculateModal] = useState(false);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showDistributeModal, setShowDistributeModal] = useState(false);
  const [showPostModal, setShowPostModal] = useState(false);
  
  // Form states
  const [taxPercentage, setTaxPercentage] = useState(0);
  const [approveNotes, setApproveNotes] = useState('');
  const [distributeData, setDistributeData] = useState({
    payment_method: 'bank_transfer',
    payment_date: new Date().toISOString().split('T')[0],
    notes: ''
  });

  useEffect(() => {
    fetchCalculation();
  }, [id]);

  const fetchCalculation = async () => {
    try {
      setLoading(true);
      const data = await getSHUCalculation(id);
      setCalculation(data);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Gagal memuat data perhitungan SHU');
      console.error('Error fetching SHU calculation:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCalculate = async () => {
    try {
      setProcessing(true);
      setError(null);
      await calculateSHU(id, { tax_percentage: taxPercentage });
      setSuccessMessage('Perhitungan SHU berhasil!');
      setShowCalculateModal(false);
      fetchCalculation();
    } catch (err) {
      setError(err.response?.data?.error || 'Gagal menghitung SHU');
    } finally {
      setProcessing(false);
    }
  };

  const handleApprove = async () => {
    try {
      setProcessing(true);
      setError(null);
      await approveSHU(id, { notes: approveNotes });
      setSuccessMessage('Perhitungan SHU berhasil disetujui!');
      setShowApproveModal(false);
      fetchCalculation();
    } catch (err) {
      setError(err.response?.data?.error || 'Gagal menyetujui SHU');
    } finally {
      setProcessing(false);
    }
  };

  const handleDistribute = async () => {
    try {
      setProcessing(true);
      setError(null);
      await distributeSHU(id, distributeData);
      setSuccessMessage('SHU berhasil didistribusikan!');
      setShowDistributeModal(false);
      fetchCalculation();
    } catch (err) {
      setError(err.response?.data?.error || 'Gagal mendistribusikan SHU');
    } finally {
      setProcessing(false);
    }
  };

  const handlePost = async () => {
    try {
      setProcessing(true);
      setError(null);
      await postSHU(id);
      setSuccessMessage('SHU berhasil diposting ke buku besar!');
      setShowPostModal(false);
      fetchCalculation();
    } catch (err) {
      setError(err.response?.data?.error || 'Gagal memposting SHU');
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!calculation) {
    return (
      <div className="container mx-auto px-4 py-6">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          Perhitungan SHU tidak ditemukan
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6">
      {/* Header */}
      <div className="flex justify-between items-start mb-6">
        <div className="flex items-center">
          <button
            onClick={() => navigate('/admin/shu')}
            className="mr-4 text-gray-600 hover:text-gray-900"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{calculation.calculation_number}</h1>
            <p className="text-gray-600 mt-1">Perhitungan SHU Tahun Fiskal {calculation.fiscal_year}</p>
          </div>
        </div>
        <span className={`px-4 py-2 rounded-full text-sm font-semibold ${getCalculationStatusColor(calculation.status)}`}>
          {getStatusLabel(calculation.status, 'calculation')}
        </span>
      </div>

      {/* Messages */}
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}
      {successMessage && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
          {successMessage}
        </div>
      )}

      {/* Action Buttons */}
      <div className="mb-6 flex gap-3">
        {calculation.status === 'draft' && (
          <button
            onClick={() => setShowCalculateModal(true)}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            Hitung SHU
          </button>
        )}
        {calculation.status === 'calculated' && (
          <button
            onClick={() => setShowApproveModal(true)}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
          >
            Setujui SHU
          </button>
        )}
        {calculation.status === 'approved' && (
          <button
            onClick={() => setShowDistributeModal(true)}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
          >
            Distribusikan SHU
          </button>
        )}
        {calculation.status === 'distributed' && (
          <button
            onClick={() => setShowPostModal(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Post ke Buku Besar
          </button>
        )}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-sm text-gray-600 mb-1">Total Pendapatan</div>
          <div className="text-2xl font-bold text-gray-900">{formatCurrency(calculation.total_revenue)}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-sm text-gray-600 mb-1">Total Beban</div>
          <div className="text-2xl font-bold text-gray-900">{formatCurrency(calculation.total_expenses)}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-sm text-gray-600 mb-1">Laba Bersih (SHU Kotor)</div>
          <div className="text-2xl font-bold text-green-600">{formatCurrency(calculation.net_profit)}</div>
        </div>
      </div>

      {/* Allocation Details */}
      <div className="bg-white rounded-lg shadow mb-6">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Alokasi Dana SHU</h2>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex justify-between p-3 bg-gray-50 rounded">
              <span className="text-gray-700">Dana Cadangan</span>
              <span className="font-semibold">{formatCurrency(calculation.reserve_fund)}</span>
            </div>
            <div className="flex justify-between p-3 bg-gray-50 rounded">
              <span className="text-gray-700">Dana Modal Anggota</span>
              <span className="font-semibold">{formatCurrency(calculation.member_capital_fund)}</span>
            </div>
            <div className="flex justify-between p-3 bg-gray-50 rounded">
              <span className="text-gray-700">Dana Transaksi Anggota</span>
              <span className="font-semibold">{formatCurrency(calculation.member_transaction_fund)}</span>
            </div>
            <div className="flex justify-between p-3 bg-gray-50 rounded">
              <span className="text-gray-700">Dana Pengurus</span>
              <span className="font-semibold">{formatCurrency(calculation.management_fund)}</span>
            </div>
            <div className="flex justify-between p-3 bg-gray-50 rounded">
              <span className="text-gray-700">Dana Karyawan</span>
              <span className="font-semibold">{formatCurrency(calculation.employee_fund)}</span>
            </div>
            <div className="flex justify-between p-3 bg-gray-50 rounded">
              <span className="text-gray-700">Dana Pendidikan</span>
              <span className="font-semibold">{formatCurrency(calculation.education_fund)}</span>
            </div>
            <div className="flex justify-between p-3 bg-gray-50 rounded">
              <span className="text-gray-700">Dana Sosial</span>
              <span className="font-semibold">{formatCurrency(calculation.social_fund)}</span>
            </div>
            <div className="flex justify-between p-3 bg-gray-50 rounded">
              <span className="text-gray-700">Dana Pembangunan</span>
              <span className="font-semibold">{formatCurrency(calculation.development_fund)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Member Distributions */}
      {calculation.distributions && calculation.distributions.length > 0 && (
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Distribusi ke Anggota ({calculation.total_members} anggota)</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">No. Voucher</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Anggota</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Simpanan</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Transaksi</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total SHU</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Pajak</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">SHU Bersih</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {calculation.distributions.map((dist) => (
                  <tr key={dist.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {dist.voucher_number}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div>{dist.member_name}</div>
                      <div className="text-xs text-gray-500">{dist.member_code}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900">
                      {formatCurrency(dist.total_member_savings)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900">
                      {formatCurrency(dist.total_member_transactions)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-semibold text-gray-900">
                      {formatCurrency(dist.total_shu_amount)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-red-600">
                      {formatCurrency(dist.tax_amount)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-semibold text-green-600">
                      {formatCurrency(dist.net_shu_amount)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getDistributionStatusColor(dist.distribution_status)}`}>
                        {getStatusLabel(dist.distribution_status, 'distribution')}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-gray-50">
                <tr>
                  <td colSpan="4" className="px-6 py-3 text-right font-semibold text-gray-900">Total Distribusi:</td>
                  <td className="px-6 py-3 text-right font-bold text-gray-900">{formatCurrency(calculation.total_distributed)}</td>
                  <td colSpan="3"></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {/* Modals */}
      {/* Calculate Modal */}
      {showCalculateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Hitung SHU</h3>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Persentase Pajak (PPh)
              </label>
              <input
                type="number"
                value={taxPercentage}
                onChange={(e) => setTaxPercentage(parseFloat(e.target.value))}
                min="0"
                max="100"
                step="0.1"
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                placeholder="0"
              />
              <p className="mt-1 text-sm text-gray-500">Masukkan 0 jika tidak dikenakan pajak</p>
            </div>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowCalculateModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg"
                disabled={processing}
              >
                Batal
              </button>
              <button
                onClick={handleCalculate}
                disabled={processing}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg disabled:opacity-50"
              >
                {processing ? 'Menghitung...' : 'Hitung'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Approve Modal */}
      {showApproveModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Setujui Perhitungan SHU</h3>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Catatan Persetujuan
              </label>
              <textarea
                value={approveNotes}
                onChange={(e) => setApproveNotes(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                placeholder="Catatan tambahan (opsional)"
              />
            </div>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowApproveModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg"
                disabled={processing}
              >
                Batal
              </button>
              <button
                onClick={handleApprove}
                disabled={processing}
                className="px-4 py-2 bg-green-600 text-white rounded-lg disabled:opacity-50"
              >
                {processing ? 'Menyetujui...' : 'Setujui'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Distribute Modal */}
      {showDistributeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Distribusikan SHU</h3>
            <div className="space-y-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Metode Pembayaran
                </label>
                <select
                  value={distributeData.payment_method}
                  onChange={(e) => setDistributeData({ ...distributeData, payment_method: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                >
                  <option value="cash">Tunai</option>
                  <option value="bank_transfer">Transfer Bank</option>
                  <option value="added_to_savings">Ditambahkan ke Simpanan</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tanggal Pembayaran
                </label>
                <input
                  type="date"
                  value={distributeData.payment_date}
                  onChange={(e) => setDistributeData({ ...distributeData, payment_date: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Catatan
                </label>
                <textarea
                  value={distributeData.notes}
                  onChange={(e) => setDistributeData({ ...distributeData, notes: e.target.value })}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  placeholder="Catatan distribusi (opsional)"
                />
              </div>
            </div>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowDistributeModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg"
                disabled={processing}
              >
                Batal
              </button>
              <button
                onClick={handleDistribute}
                disabled={processing}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg disabled:opacity-50"
              >
                {processing ? 'Mendistribusikan...' : 'Distribusikan'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Post to Ledger Modal */}
      {showPostModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Post ke Buku Besar</h3>
            <p className="text-gray-700 mb-4">
              Apakah Anda yakin ingin memposting SHU ini ke buku besar? 
            </p>
            <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-blue-700">
                    <strong>Catatan:</strong> Proses ini akan:
                  </p>
                  <ul className="mt-2 text-sm text-blue-700 list-disc list-inside">
                    <li>Memposting jurnal alokasi SHU</li>
                    <li>Membuat jurnal penutup untuk akun Pendapatan dan Beban</li>
                    <li>Menutup akun Pendapatan (4.x.xx) dan Beban (5.x.xx) ke SHU Tahun Berjalan</li>
                  </ul>
                  <p className="mt-2 text-sm text-blue-700 font-semibold">
                    Setelah diposting, jurnal tidak dapat diubah atau dibatalkan.
                  </p>
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowPostModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg"
                disabled={processing}
              >
                Batal
              </button>
              <button
                onClick={handlePost}
                disabled={processing}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg disabled:opacity-50"
              >
                {processing ? 'Memposting...' : 'Post ke Buku Besar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SHUCalculationDetail;
